import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TrainingTemplateService } from '../../../services/training-template.service';
import { OrganisationUnitService } from '../../../services/organisation-unit.service';
import { forkJoin, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { OrganisationUnitModel } from '../../../models/organisation-unit/organisation-unit.model';
import { shareReplay, switchMap } from 'rxjs/operators';
import { TrainingGroupModel } from '../../../models/training-group.model';
import { PersonService } from '../../../services/person.service';
import { TrainingGroupService } from '../../../services/training-group.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LoadingService } from '../../../services/loading.service';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { FileUtilService } from '../../../services/file-util.service';
import { TrainingTypeModel } from '../../../models/training/training-type.model';
import { TrainingLocationTypeModel } from '../../../models/training/training-location-type.model';
import { GoogleMapsService } from '../../../services/google-maps.service';
import { TimeService } from '../../../services/time.service';
import { TrainingHelperService } from '../../../services/training-helper.service';
import { TrainingTemplateFullModel } from 'src/app/models/training-template/training-template-full.model';
import { TrainingTemplateCreationAndUpdateModel } from 'src/app/models/training-template/training-template-creation-and-update.model';
import { TrainingLocationCreationAndUpdateModel } from 'src/app/models/training/training-location-creation-and-update.model';
import { TranslateService } from '@ngx-translate/core';
import { ExternalContactService } from '../../../services/external-contacts.service';
import { IntegrationsService } from '../../../services/integration/integrations.service';
import { IntegrationTypeModel } from '../../../models/integration/integration-type.model';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import { UserService } from 'src/app/services/user.service';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { TrainingCategoryModel } from 'src/app/models/training-category/training-category.model';
import { IDD_MIN_TRAINING_DURATION_MINUTES } from 'src/app/constants/idd.constants';
import validationEngine from 'devextreme/ui/validation_engine';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { TenantSubscriptionService } from 'src/app/services/tenants/tenant-subscription.service';
import { EditTrainingParticipationQuestionsComponent } from 'src/app/components/edit-training-participation-questions/edit-training-participation-questions.component';
import { EventParticipationQuestionTemplateModel } from 'src/app/models/event-participations/event-participation-question-template.model';
import { ParticipationQuestionTemplateService } from 'src/app/services/participation-question-template.service';
import { TrainingReminderTypeModel } from 'src/app/models/training/training-reminder-type.model';
import { ReportPreviewService } from 'src/app/services/report-preview.service';
import { ReportKeyModel } from 'src/app/models/report-preview/report-key.model';
import { ReportKeyTypeModel } from 'src/app/models/report-preview/report-key-type.model';
import { PreviewResponse } from 'src/app/models/report-preview/preview-response.model';
import { UserContextService } from 'src/app/services/user-context.service';
import { TextTemplateTypeModel } from 'src/app/models/text-templates/text-template-type.model';
import { PublishingMode } from 'src/app/models/training/publishing-mode.model';
import { StandardTrainingCategoryService } from 'src/app/services/training-categories/standard-training-category.service';
import { StandardTrainingCategoryViewModel } from 'src/app/models/standard-training-category/standard-training-category-view.model';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { PageStateService } from 'src/app/services/page-state.service';
import { RoleModel } from 'src/app/models/person/role.model';
import { TenantSubscriberModel } from 'src/app/models/subscription/tenant-subscriber.model';
import { RoleService } from 'src/app/services/role.service';
import { SubscriptionService } from 'src/app/services/subscriptions/subscription.service';
import { EventVisibilityModeModel } from 'src/app/models/event-visibility-mode.model';
import { TenantContextService } from 'src/app/services/tenant-context.service';

interface SelectOption {
  id: string;
  name: string;
  selected?: boolean;
}

@Component({
  selector: 'app-training-template-form',
  templateUrl: './training-template-form.component.html',
  styleUrls: ['./training-template-form.component.scss'],
  standalone: false
})
export class TrainingTemplateFormComponent implements OnInit {
  @ViewChild('descriptionForm', { static: true })
  descriptionForm!: DxFormComponent;
  @ViewChild('settingsForm', { static: true }) settingsForm!: DxFormComponent;
  @ViewChild('locationForm', { static: true }) locationForm!: DxFormComponent;

  private timePicker!: ElementRef<HTMLElement>;
  @ViewChild('timePicker') set timePickerContent(
    content: ElementRef<HTMLElement>
  ) {
    if (content) {
      this.timePicker = content;
    }
  }

  @ViewChild('participationQuestionEditorComponent')
  participationQuestionEditorComponent!: EditTrainingParticipationQuestionsComponent;

  private googlePlaceinputChanged = new Subject<string>();
  googleAutocompleteResults$ = this.googlePlaceinputChanged.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    switchMap((searchTerm) => this.mapsService.getAutocomplete(searchTerm))
  );

  isDuplicate: boolean = false;
  duplicateTrainingTemplateId!: string;
  isViewMode: boolean = false;
  googlePlaces: any[] = [];
  googleSearchText: string = '';
  TrainingLocationType = TrainingLocationTypeModel;
  TrainingTypeModel = TrainingTypeModel;
  editingTrainingTemplateId!: string;

  questionTemplates: EventParticipationQuestionTemplateModel[] = [];
  organizationUnits!: OrganisationUnitModel[];
  certificateOrganizationUnits!: OrganisationUnitModel[];
  trainingGroups!: TrainingGroupModel[];
  trainingCategories!: TrainingCategoryModel[];
  standardTrainingCategories!: StandardTrainingCategoryViewModel[];
  roles!: RoleModel[];
  subscribers!: TenantSubscriberModel[];
  trainers!: SelectOption[];
  trainingTypeOptions!: DxSelectBoxTypes.Properties;
  trainingLocationTypeOptions!: DxSelectBoxTypes.Properties;
  integrations: Array<any> = [];
  isDescriptionExpanded: boolean = false;
  isCertificateDescriptionExpanded: boolean = false;
  selectedDescriptionTab: number = 0;
  isWbdConfigured: boolean = false;
  learningTypeOptions!: DxSelectBoxTypes.Properties;
  learningContentOptions!: DxSelectBoxTypes.Properties;
  isTenantPublishedToSubscribers: boolean = false;
  isPrivatePublishing: boolean = false;
  personCount: number = 0;

  showPreviewReportViewer = false;
  reportUrl: string = '';
  
  showDeleteTemplateModal = false;
  modalConfirmationOptions: ConfirmationDialogData | null = null;
  
  visibilityModes = EventVisibilityModeModel;

  certificateFontFamily: string | null = null;
  certificateFontSize: string | null = null;

  isTrainingTimeValid: boolean = true;
  trainingTimeAdapter = {
    getValue: () => {
      return this.trainingTemplateForm.iddHours! * 60 + this.trainingTemplateForm.iddMinutes!;
    },
  };

  descriptionTabs: any[] = [
    {
      id: 0,
      text: this.translateService.instant('TRAINING.FORM.EXTENDED-DESCRIPTION'),
    },
    {
      id: 1,
      text: this.translateService.instant(
        'TRAINING.FORM.TEXT-FOR-CERTIFICATES'
      ),
    },
  ];

  reminderOptions: { value: TrainingReminderTypeModel; label: string; selected?: boolean; }[] = [
    { 
      value: TrainingReminderTypeModel.ConfirmationEmail, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.CONFIRMATION-EMAIL'
      ),
    },
    { 
      value: TrainingReminderTypeModel.OneWeekBefore, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.1-WEEK-BEFORE'
      ),
    },
    { 
      value: TrainingReminderTypeModel.OneDayBefore, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.1-DAY-BEFORE'
      ),
    },
    { 
      value: TrainingReminderTypeModel.ThirtyMinutesBefore, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.30-MINUTES-BEFORE'
      ),
    },
    { 
      value: TrainingReminderTypeModel.FifteenMinutesBefore, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.15-MINUTES-BEFORE'
      ),
    }
  ];

  trainingTemplateForm: TrainingTemplateCreationAndUpdateModel = {
    name: '',
    description: '',
    descriptionExtended: null,
    descriptionCertificate: null,
    trainerIds: [],
    trainingType: null,
    iddTimeMinutes: null,
    iddHours: 0,
    iddMinutes: 0,
    sendCertificateLink: true,
    sendFeedbackLink: true,
    organisationUnitLocationIds: [],
    trainingGroupIds: [],
    trainingCategoryIds: [],
    maximumMembers: null,
    trainingLocation: {
      locationType: null,
      location: '',
      suffix: '',
      placeId: '',
      latitude: null,
      longitude: null,
      calendarEventId: null
    },
    emailBody: '',
    imageBase64: null,
    parentTemplateIds: [],
    lernart: null,
    lerninhalt: null,
    allowUnknownUsersToJoin: false,
    questions: null,
    certificateFooterLocationId: null,
    reminders: [],
    publishingMode: PublishingMode.ByInvitationOnly,
    standardTrainingCategoryIds: [],
    roleIds: [],
    subscriptionIds: [],
    visibilityMode: null,
    allowSharingWithSubscribers: false
  };

  selectedOrganizationUnitIds: string[] = [];

  invitationTextTemplateType: TextTemplateTypeModel =
    TextTemplateTypeModel.AddParticipants;

  descriptionTextTemplateType: TextTemplateTypeModel =
    TextTemplateTypeModel.ExtendedDescription;

  get getTrainingLocationLabel() {
    if(!this.trainingTemplateForm.trainingLocation)
      return '';

    const isWeb =
      this.trainingTemplateForm.trainingLocation.locationType ===
        TrainingLocationTypeModel.Web;

    return isWeb
      ? this.translateService.instant('TRAINING.FORM.PLATFORM')
      : this.translateService.instant('COMMON.FIELDS.TRAINING-LOCATION');
  }

  get getTrainingLocationDetailsLabel() {
    if(!this.trainingTemplateForm.trainingLocation)
      return '';

    const isWebOrExternalEvent =
      this.trainingTemplateForm.trainingLocation.locationType ===
        TrainingLocationTypeModel.Web ||
      this.trainingTemplateForm.trainingLocation.locationType ===
        TrainingLocationTypeModel.ExternalEvent;

    return isWebOrExternalEvent
      ? this.translateService.instant('TRAINING.FORM.LINK-OR-MEETING')
      : this.translateService.instant('COMMON.FIELDS.LOCATION-DETAILS');
  }

  get isVisibleToEveryone() {
    return this.isAllOrganizationUnitsSelected &&
      this.trainingTemplateForm.trainingGroupIds.length == 0 &&
      this.trainingTemplateForm.subscriptionIds.length == 0 &&
      this.trainingTemplateForm.roleIds.length == 0;
  }

  get isAllOrganizationUnitsSelected() {
    const selectedOrgUnitCount = 
      this.selectedOrganizationUnitIds.length;
      
    return selectedOrgUnitCount == 0
      || selectedOrgUnitCount == this.organizationUnits.length;
  }

   get allowUnknownUsersToJoinDisabled(): boolean {
    return !this.isViewMode && (
       (
        this.trainingTemplateForm.publishingMode === PublishingMode.PublishToSubscribers &&
        this.trainingTemplateForm.visibilityMode === EventVisibilityModeModel.Subscribers 
      ) ||
      (
        this.trainingTemplateForm.publishingMode === PublishingMode.DeployToTargetGroup &&
        this.trainingTemplateForm.allowSharingWithSubscribers
      )
    );
  }

  public get isIntegrationLocationType(): boolean {
    const locationType = 
      this.trainingTemplateForm.trainingLocation?.locationType;

    return (
      locationType !== TrainingLocationTypeModel.Address &&
      locationType !== TrainingLocationTypeModel.Web &&
      locationType !== TrainingLocationTypeModel.ExternalEvent
    );
  }

  get isExternalEvent() {
    return this.trainingTemplateForm.trainingLocation?.locationType ===
      TrainingLocationTypeModel.ExternalEvent;
  }

  constructor(
    private organisationUnitService: OrganisationUnitService,
    private personService: PersonService,
    private externalContactService: ExternalContactService,
    private trainingGroupService: TrainingGroupService,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private router: Router,
    private loadingService: LoadingService,
    private fileUtilService: FileUtilService,
    private trainingTemplateService: TrainingTemplateService,
    readonly trainingHelperService: TrainingHelperService,
    private mapsService: GoogleMapsService,
    private translateService: TranslateService,
    private integrationsService: IntegrationsService,
    private userService: UserService,
    private trainingCategoryService: TrainingCategoryService,
    private reportPreviewService: ReportPreviewService,
    private tenantSubscriptionService: TenantSubscriptionService,
    private questionTemplateService: ParticipationQuestionTemplateService,
    public userContext: UserContextService,
    private standardTrainingCategoryService: StandardTrainingCategoryService,
    private pageStateService: PageStateService,
    private roleService: RoleService,
    private subscriptionService: SubscriptionService,
    private tenantContext: TenantContextService
  ) {
    this.autocompleteSearchFilter = this.autocompleteSearchFilter.bind(this);
    this.editingTrainingTemplateId = this.route.snapshot.params['id'];

    if (history.state.duplicateId) {
      this.isDuplicate = true;
      this.duplicateTrainingTemplateId = history.state.duplicateId;
    }

    const path = this.route.snapshot.routeConfig!.path!;
    if (
      !path.includes('training-template/add') &&
      !path.includes('training-template/edit')
    ) {
      this.isViewMode = true;
    }

    const { fontFamily, fontSize } = 
      this.tenantContext.getCertificateFontSettings();
    this.certificateFontFamily = fontFamily;
    this.certificateFontSize = fontSize;

    const formData = this.loadingService
      .load(
        forkJoin({
          trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
          organizationUnits:
            this.organisationUnitService.getAllOrganisationUnits(),
          trainers: this.personService.getAllPersons(),
          externalContacts: this.externalContactService.getAllContacts(),
          integrations: this.integrationsService.getIntegrations(),
          isWbdConfigured: this.userService.getWbdConfigured(),
          trainingCategories:
            this.trainingCategoryService.getAllTrainingCategories(),
          questionTemplates: this.questionTemplateService.getQuestionTemplates(),
          tenantSubscription: this.tenantSubscriptionService.getCurrentTenantSubscription(),
          personCount: this.personService.getPersonCount(),
          standardTrainingCategories:
            this.standardTrainingCategoryService.getAllStandardTrainingCategories(
              this.userContext.currentPersonCulture ?? 'en'
            ),
          roles: this.roleService.getAllRoles(),
          subscribers: this.subscriptionService.getApprovedTenantSubscribers()
        })
      )
      .pipe(shareReplay(1));

    formData.subscribe(
      ({
        trainingGroups,
        organizationUnits,
        trainers,
        externalContacts,
        integrations,
        isWbdConfigured,
        trainingCategories,
        questionTemplates,
        tenantSubscription,
        personCount,
        standardTrainingCategories,
        roles,
        subscribers
      }) => {
        this.trainingGroups = trainingGroups;
        this.organizationUnits = organizationUnits;
        this.integrations = integrations;
        this.isWbdConfigured = isWbdConfigured;
        this.questionTemplates = questionTemplates;
        this.trainingCategories = trainingCategories;
        this.roles = roles;
        this.subscribers = subscribers;
        this.personCount = personCount;

        this.isTenantPublishedToSubscribers = 
          tenantSubscription?.publishToSubscribers ?? false;
        this.isPrivatePublishing =
          tenantSubscription?.publishToSubscribers === true &&
          tenantSubscription?.isPrivatePublishing === true;


        this.standardTrainingCategories = standardTrainingCategories.map(x => ({
          id: x.id,
          name: x.name ?? '',
          description: x.description ?? '',
          descriptionExtended: x.descriptionExtended ?? '',
          type: x.type,
          parentId: x.parentId,
          imageFile: x.imageFile
        }));

        this.certificateOrganizationUnits = this.organisationUnitService
          .buildCertificateTree(organizationUnits).map(u => ({
            ...u,
            selected: u.id === this.trainingTemplateForm.certificateFooterLocationId
          }));

        this.trainers = trainers.map((trainer) => ({
          id: trainer.id,
          name: `${trainer.firstName} ${trainer.lastName}`,
        }));

        externalContacts.forEach((contact) => {
          this.trainers.push({
            id: contact.id,
            name: `${contact.firstName} ${contact.lastName}`,
          });
        });

        this.setDropdownOptions();

        if (this.duplicateTrainingTemplateId) {
          this.loadingService
            .load(
              this.trainingTemplateService.getTrainingTemplateClone(
                this.duplicateTrainingTemplateId
              )
            )
            .subscribe((trainingTemplate) => {
              this.patchForm(trainingTemplate);
            });
        }

        if (this.editingTrainingTemplateId) {
          this.loadingService
            .load(
              this.trainingTemplateService.getTrainingTemplate(
                this.editingTrainingTemplateId
              )
            )
            .subscribe((trainingTemplate) => {
              this.patchForm(trainingTemplate);
            });
        }
      }
    );
  }

  ngOnInit(): void {
    this.googleAutocompleteResults$.subscribe((results) => {
      this.googlePlaces = results;
    });
  }

  onPublishingModeChanged(publishingMode: PublishingMode) {
    this.trainingTemplateForm.publishingMode = publishingMode;

    if (
      publishingMode === PublishingMode.DeployToTargetGroup
    ) {
      this.trainingTemplateForm.visibilityMode ??= EventVisibilityModeModel.Everyone;
    } else if (
      publishingMode === PublishingMode.PublishToSubscribers
    ) {
      this.trainingTemplateForm.visibilityMode ??= EventVisibilityModeModel.Subscribers;
    } else {
      this.trainingTemplateForm.visibilityMode = null;
    }
  }

  onVisibilityModeChanged(visibilityMode: EventVisibilityModeModel | null) {
    this.trainingTemplateForm.visibilityMode = visibilityMode;

    if (
      visibilityMode === EventVisibilityModeModel.Subscribers
    ) {
      this.trainingTemplateForm.allowUnknownUsersToJoin = true;
    } 
  }

  onAllowSharingWithSubscribersChanged(
    allowSharingWithSubscribers: boolean
  ) {
    this.trainingTemplateForm.allowSharingWithSubscribers = 
      allowSharingWithSubscribers;

    if (allowSharingWithSubscribers) {
      this.trainingTemplateForm.allowUnknownUsersToJoin = true;
    } 
  }

  navigateBack(trainingTemplateId?: string): void {
    if (this.isViewMode) {
      this.pageStateService.setFlag(
        PageStateFlags.RestoreTrainingTemplatesOverview
      );

      this.router.navigate(['/training']);
    } else {
      this.router.navigate([
        '/training-template/' +
          (trainingTemplateId || this.editingTrainingTemplateId),
      ]);
    }
  }

  validateTrainingTime(e: any): boolean {
    const value = e.value;

    return !value || value >= IDD_MIN_TRAINING_DURATION_MINUTES;
  }

  onFormSubmit(): void {
    let isFormValid = this.isFormValid();

    if(isFormValid) {
      const questions = this.participationQuestionEditorComponent
       ? this.participationQuestionEditorComponent.getQuestions()
       : null;

      this.adjustTargetGroupBeforeSubmit();

      this.trainingTemplateForm.organisationUnitLocationIds = [];

      if (!this.selectedOrganizationUnitIds.length) {
        this.organizationUnits.forEach(unit =>
          this.trainingTemplateForm.organisationUnitLocationIds.push(unit.id)
        );
      } else {
        this.selectedOrganizationUnitIds.forEach(id =>
          this.trainingTemplateForm.organisationUnitLocationIds.push(id)
        );
      }

      if(
        this.trainingTemplateForm.trainingLocation &&
        this.trainingTemplateForm.trainingLocation.locationType == null
      ) {
        this.trainingTemplateForm.trainingLocation = null;
      }

      if (this.editingTrainingTemplateId) {
        const trainingTemplate = {
          ...this.trainingTemplateForm,
          imageBase64: this.trainingTemplateForm.imageBase64
            ? this.trainingTemplateForm.imageBase64!.split(',')[1]
            : null,
          questions: questions
        };

        if (trainingTemplate.iddHours && trainingTemplate.iddMinutes) {
          delete trainingTemplate.iddHours;
          delete trainingTemplate.iddMinutes;
        }

        if (this.trainingTemplateForm.trainingType === 0) {
          trainingTemplate.iddTimeMinutes =
            this.trainingTemplateForm.iddHours! * 60 +
            this.trainingTemplateForm.iddMinutes!;
        } else {
          trainingTemplate.iddTimeMinutes = 0;
          trainingTemplate.lernart = null;
          trainingTemplate.lerninhalt = null;
        }

        const result = this.trainingTemplateService.updateTrainingTemplate(
          this.editingTrainingTemplateId,
          trainingTemplate
        );

        this.loadingService.load(result).subscribe({
          next: () => {
            this.toastrService.success(
              this.translateService.instant(
                'TRAINING-TEMPLATE.FORM.TEMPLATE-UPDATED'
              )
            );
            this.navigateViewMode(this.editingTrainingTemplateId);
          },
          error: () =>
            this.toastrService.error(
              this.translateService.instant('COMMON.UNKNOWN-ERROR')
            ),
        });
      } else {
        const trainingTemplate: TrainingTemplateCreationAndUpdateModel = {
          ...this.trainingTemplateForm,
          imageBase64: this.trainingTemplateForm.imageBase64
            ? this.trainingTemplateForm.imageBase64!.split(',')[1]
            : null,
          questions: questions
        };
        if (!trainingTemplate.imageBase64) {
          delete trainingTemplate.imageBase64;
        }
        if (this.trainingTemplateForm.trainingType === 0) {
          trainingTemplate.iddTimeMinutes =
            this.trainingTemplateForm.iddHours! * 60 +
            this.trainingTemplateForm.iddMinutes!;
        } else {
          trainingTemplate.iddTimeMinutes = 0;
          trainingTemplate.lernart = null;
          trainingTemplate.lerninhalt = null;
        }
        const result =
          this.trainingTemplateService.addTrainingTemplate(trainingTemplate);
        this.loadingService.load(result).subscribe({
          next: (trainingTemplate) => {
            this.toastrService.success(
              this.translateService.instant(
                'TRAINING-TEMPLATE.FORM.TEMPLATE-CREATED'
              )
            );
            this.navigateViewMode(trainingTemplate.trainingTemplate.id);
          },
          error: () =>
            this.toastrService.error(
              this.translateService.instant('COMMON.FIELDS.UNKNOWN-ERROR')
            ),
        });
      }
    }
  }

  private adjustTargetGroupBeforeSubmit(): void {
    if (
      this.trainingTemplateForm.publishingMode === PublishingMode.ByInvitationOnly
    ) {
      this.selectedOrganizationUnitIds = [];
      this.trainingTemplateForm.trainingGroupIds = [];
      this.trainingTemplateForm.trainingCategoryIds = [];
      this.trainingTemplateForm.standardTrainingCategoryIds = [];
      this.trainingTemplateForm.roleIds = [];
      this.trainingTemplateForm.subscriptionIds = [];
    } else if (
      this.trainingTemplateForm.publishingMode === PublishingMode.PublishToCatalogue
    ) {
      this.selectedOrganizationUnitIds = [];
      this.trainingTemplateForm.trainingGroupIds = [];
      this.trainingTemplateForm.roleIds = [];
    }

    if (
      this.trainingTemplateForm.visibilityMode === EventVisibilityModeModel.Everyone
    ) {
      this.selectedOrganizationUnitIds = [];
      this.trainingTemplateForm.trainingGroupIds = [];
      this.trainingTemplateForm.roleIds = [];
    }

    if (
      this.trainingTemplateForm.visibilityMode === EventVisibilityModeModel.Subscribers
    ) {
      this.trainingTemplateForm.trainingCategoryIds = [];
      this.selectedOrganizationUnitIds = [];
      this.trainingTemplateForm.trainingGroupIds = [];
      this.trainingTemplateForm.roleIds = [];
    }

    if (
      this.trainingTemplateForm.visibilityMode !== EventVisibilityModeModel.Subscribers &&
      !this.trainingTemplateForm.allowSharingWithSubscribers
    ) {
      this.trainingTemplateForm.standardTrainingCategoryIds = [];
      this.trainingTemplateForm.subscriptionIds = [];
    }

     if (this.isExternalEvent) {
      this.trainingTemplateForm.maximumMembers = null;
      this.trainingTemplateForm.reminders = [];
      this.trainingTemplateForm.sendCertificateLink = false;
      this.trainingTemplateForm.sendFeedbackLink = false;
      this.trainingTemplateForm.allowUnknownUsersToJoin = false;
    }
  }

  isFormValid(): boolean {
    const descriptionResult = this.descriptionForm.instance.validate();
    if (!descriptionResult.isValid) {
      this.scrollDxComponent(this.descriptionForm);
      return false;
    }

    const settingsResult = this.settingsForm.instance.validate();
    if (!settingsResult.isValid) {
      this.scrollDxComponent(this.settingsForm);
      return false;
    }

    const locationResult = this.locationForm.instance.validate();
    if (!locationResult.isValid) {
      this.scrollDxComponent(this.locationForm);
      return false;
    }

    const group = validationEngine.getGroupConfig('customFields');

    if (group) {
      const customFieldsResult = validationEngine.validateGroup('customFields');

      if (!customFieldsResult.isValid) {
        this.scrollToFirstBrokenRule(customFieldsResult);
        return false;
      }
    }

    const trainingTimeResult =
      this.trainingTemplateForm.trainingType !== TrainingTypeModel.IDD
        ? { isValid: true }
        : validationEngine.validateGroup('trainingTimeGroup');

    this.isTrainingTimeValid = !!trainingTimeResult.isValid;

    if (!trainingTimeResult.isValid) {
      this.timePicker?.nativeElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return false;
    }

    if(
      this.participationQuestionEditorComponent && 
      !this.participationQuestionEditorComponent.validate()
    ) {
      return false;
    }

    return true;
  }

  private scrollDxComponent(component: any): void {
    const element = component?.instance?.element?.();
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }

  private scrollToFirstBrokenRule(result: any): void {
    if (!result?.brokenRules?.length) return;

    const firstBrokenRule = result.brokenRules[0] as any;
    const element = firstBrokenRule?.validator?.$element?.();

    element?.[0]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    element?.[0]?.focus();
  }

  setDropdownOptions() {
    const defaultSelectOptions = {
      showClearButton: true,
      stylingMode: 'outlined',
      displayExpr: 'name',
      valueExpr: 'id',
    } as DxSelectBoxTypes.Properties;
    this.trainingTypeOptions = {
      ...defaultSelectOptions,
      dataSource: [
        {
          id: 0,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.IDD'
          ),
        },
        {
          id: 1,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
          ),
        },
        {
          id: 2,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
          ),
        },
      ],
      placeholder: '',
    };

    const trainingLocationTypeOptionsDataSource = [
      {
        id: TrainingLocationTypeModel.Address,
        name: this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.FACE-TO-FACE'
        ),
      },
      {
        id: TrainingLocationTypeModel.Web,
        name: this.translateService.instant('COMMON.FIELDS.EVENT-TYPES.ONLINE'),
      },
      {
        id: TrainingLocationTypeModel.ExternalEvent,
        name: this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.EXTERNAL-EVENT'
        ),
      },
    ];

    for (let integration of this.integrations) {
      if (integration.type == IntegrationTypeModel.GoToWebinar) {
        trainingLocationTypeOptionsDataSource.push({
          id: TrainingLocationTypeModel.GoToWebinar,
          name: this.translateService.instant(
            'COMMON.FIELDS.EVENT-TYPES.GO-TO-WEBINAR'
          ),
        });
      }
      if (integration.type == IntegrationTypeModel.Webex) {
        trainingLocationTypeOptionsDataSource.push({
          id: TrainingLocationTypeModel.Webex,
          name: this.translateService.instant(
            'COMMON.FIELDS.EVENT-TYPES.WEBEX-WEBINAR'
          ),
        });
      }
      if (integration.type == IntegrationTypeModel.MsTeams) {
        trainingLocationTypeOptionsDataSource.push({
          id: TrainingLocationTypeModel.MsTeamsWebinar,
          name: this.translateService.instant(
            'COMMON.FIELDS.EVENT-TYPES.MSTEAMS'
          ),
        });
      }
      if (integration.type == IntegrationTypeModel.MsTeamsMeetings) {
        trainingLocationTypeOptionsDataSource.push({
          id: TrainingLocationTypeModel.MsTeamsMeetings,
          name: 'Teams Meeting'
          // name: this.translateService.instant(
          //   'COMMON.FIELDS.EVENT-TYPES.MSTEAMS'
          // ),
        });
      }
      if (integration.type == IntegrationTypeModel.Zoom) {
        trainingLocationTypeOptionsDataSource.push({
          id: TrainingLocationTypeModel.Zoom,
          name: this.translateService.instant(
            'COMMON.FIELDS.EVENT-TYPES.ZOOM'
          ),
        });
      }
    }

    this.trainingLocationTypeOptions = {
      ...defaultSelectOptions,
      onItemClick: this.onTrainingLocationTypeSelected.bind(this),
      dataSource: trainingLocationTypeOptionsDataSource,
      placeholder: '',
    };

    this.learningTypeOptions = {
      ...defaultSelectOptions,
      showClearButton: true,
      dataSource: [
        {
          id: 1,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARNING-TYPES.FACE-TO-FACE'
          ),
        },
        {
          id: 2,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARNING-TYPES.INDIVIDUAL-TRAINING'
          ),
        },
        {
          id: 3,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARNING-TYPES.BLENDED-LEARNING'
          ),
        },
        {
          id: 4,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARNING-TYPES.CONTROLLED-ELEARNING'
          ),
        },
        {
          id: 5,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARNING-TYPES.SELF-DIRECTED-ELEARNING'
          ),
        },
        {
          id: 6,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARNING-TYPES.LEARNING-AT-WORK'
          ),
        },
      ],
      placeholder: '',
    };
    this.learningContentOptions = {
      ...defaultSelectOptions,
      showClearButton: true,
      dataSource: [
        {
          id: 1,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARING-CONTENT-TYPES.PENSION-INSURANCE'
          ),
        },
        {
          id: 2,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARING-CONTENT-TYPES.NURSING-INSURANCE'
          ),
        },
        {
          id: 3,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARING-CONTENT-TYPES.DAMAGE-INSURANCE'
          ),
        },
        {
          id: 4,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARING-CONTENT-TYPES.CUSTOMER-PENSION-PROVISION'
          ),
        },
        {
          id: 5,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARING-CONTENT-TYPES.CUSTOMER-PROPERTY'
          ),
        },
        {
          id: 6,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARING-CONTENT-TYPES.CROSS-DIVISIONAL'
          ),
        },
        {
          id: 7,
          name: this.translateService.instant(
            'COMMON.FIELDS.LEARING-CONTENT-TYPES.ADVISORY-COMPETENCE'
          ),
        },
      ],
      placeholder: '',
    };
  }

  setTreeViewValues() {
    this.trainingGroups.forEach((trainingGroup) => {
      if (
        this.trainingTemplateForm.trainingGroupIds.indexOf(trainingGroup.id) >
        -1
      ) {
        trainingGroup.selected = true;
      }
    });

    this.organizationUnits.forEach((unit) => {
      if (this.selectedOrganizationUnitIds.indexOf(unit.id) > -1) {
        unit.selected = true;
      }
    });

    this.trainers.forEach((trainer) => {
      if (this.trainingTemplateForm.trainerIds.indexOf(trainer.id) > -1) {
        trainer.selected = true;
      }
    });

    this.trainingCategories.forEach((trainingCategory) => {
      if (
        this.trainingTemplateForm.trainingCategoryIds.indexOf(
          trainingCategory.id
        ) > -1
      ) {
        trainingCategory.selected = true;
      }
    });

    this.standardTrainingCategories.forEach((trainingCategory) => {
      if (
        this.trainingTemplateForm.standardTrainingCategoryIds.indexOf(
          trainingCategory.id
        ) > -1
      ) {
        trainingCategory.selected = true;
      }
    });

     this.roles.forEach((role) => {
      if (
        this.trainingTemplateForm.roleIds.indexOf(role.id) > -1
      ) {
        role.selected = true;
      }
    });

    this.subscribers.forEach((subscriber) => {
      if (
        this.trainingTemplateForm.subscriptionIds.indexOf(subscriber.subscriptionId) > -1
      ) {
        subscriber.selected = true;
      }
    });

    this.reminderOptions.forEach((reminder) => {
      if (this.trainingTemplateForm.reminders.indexOf(reminder.value) > -1) {
        reminder.selected = true;
      }
    });
  }

  openDeleteTemplateConfirmationModal() {
    this.modalConfirmationOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.YES'),
      message: this.translateService.instant(
        'TRAINING-TEMPLATE.DELETE-TRAINING-TEMPLATE-DIALOG.TEXT'
      ),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
    };
    this.showDeleteTemplateModal = true;
  }

  onDeleteTemplateClick(): void {
    this.loadingService
      .load(this.trainingTemplateService.deleteTrainingTemplate(this.editingTrainingTemplateId))
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translateService.instant('TRAINING-TEMPLATE.FORM.TEMPLATE-DELETED')
          );
          this.navigateBack();
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('TRAINING-TEMPLATE.FORM.TEMPLATE-COULD-NOT-BE-DELETED')
          );
        },
      });
  }

  onTrainingLocationTypeSelected() {
    if(this.trainingTemplateForm.trainingLocation) {
      this.trainingTemplateForm.trainingLocation.location = '';
      this.trainingTemplateForm.trainingLocation.suffix = '';
      this.trainingTemplateForm.trainingLocation.placeId = '';
      this.trainingTemplateForm.trainingLocation.latitude = null;
      this.trainingTemplateForm.trainingLocation.longitude = null;
      this.trainingTemplateForm.trainingLocation.calendarEventId = null;
    }
    this.trainingTemplateForm.questions = null;
    this.googleSearchText = '';

    if (this.isExternalEvent) {
      this.trainingTemplateForm.trainerIds = [];
    }
  }

  onTrainerSelected(e: any) {
    if (e.node.selected) {
      this.trainingTemplateForm.trainerIds = [
        ...this.trainingTemplateForm.trainerIds,
        e.node.key,
      ];
    } else {
      this.trainingTemplateForm.trainerIds =
        this.trainingTemplateForm.trainerIds.filter((id) => id !== e.node.key);
    }
  }

  onAvatarUploaded(files: any) {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.trainingTemplateForm!.imageBase64 = reader!.result!.toString();
    };
    reader.readAsDataURL(file);
  }

  handleTimePicker(value: { hours: number; minutes: number }) {
    this.trainingTemplateForm.iddHours = value.hours;
    this.trainingTemplateForm.iddMinutes = value.minutes;

    const trainingTimeGroupConfig =
      validationEngine.getGroupConfig('trainingTimeGroup');
    if (this.trainingTemplateForm.trainingType === TrainingTypeModel.IDD && trainingTimeGroupConfig) {
      const isValid =
        validationEngine.validateGroup('trainingTimeGroup').isValid;

      if (isValid) {
        this.isTrainingTimeValid = true;
      }
    }
  }

  patchForm(trainingTemplate: TrainingTemplateFullModel) {
    if (
      trainingTemplate.organisationUnits.length === this.organizationUnits.length &&
      this.isViewMode
    ) {
      this.selectedOrganizationUnitIds = ['0'];
      // @ts-ignore
      this.organizationUnits = [
        {
          id: '0',
          name: this.translateService.instant('COMMON.ALL'),
          parentId: null,
          type: 1,
          description: '',
          reportFooter: '',
          imageFile: null,
          topLevel: true,
        },
      ];
    } else {
      this.selectedOrganizationUnitIds = trainingTemplate.organisationUnits.map(
        (unit) => unit.id
      );
    }

    // this.trainingTemplateForm.organisationUnitLocationIds =
    //   trainingTemplate.organisationUnits.map(
    //     (organizationUnit) => organizationUnit.id
    //   );
    this.trainingTemplateForm.name = trainingTemplate.trainingTemplate.name;
    this.trainingTemplateForm.trainingType =
      trainingTemplate.trainingTemplate.trainingType;
    this.trainingTemplateForm.description =
      trainingTemplate.trainingTemplate.description!;
    this.trainingTemplateForm.descriptionExtended =
      trainingTemplate.trainingTemplate.descriptionExtended!;
    this.trainingTemplateForm.descriptionCertificate =
      trainingTemplate.trainingTemplate.descriptionCertificate!;
    this.trainingTemplateForm.trainerIds = trainingTemplate.trainers.map(
      (trainer) => trainer.id
    );
    this.trainingTemplateForm.trainingGroupIds =
      trainingTemplate.trainingGroups.map((trainingGroup) => trainingGroup.id);
    this.trainingTemplateForm.trainingCategoryIds =
      trainingTemplate.trainingCategories.map(
        (trainingCategory) => trainingCategory.id
      );
    this.trainingTemplateForm.standardTrainingCategoryIds = 
      trainingTemplate.standardTrainingCategories.map(
        (trainingCategory) => trainingCategory.id
      );
    this.trainingTemplateForm.roleIds = trainingTemplate.roles.map(
      (role) => role.id
    );
    this.trainingTemplateForm.subscriptionIds = trainingTemplate.subscriptions.map(
      (subscription) => subscription.id
    );
    this.trainingTemplateForm.sendCertificateLink =
      trainingTemplate.trainingTemplate.sendCertificateLink;
    this.trainingTemplateForm.sendFeedbackLink =
      trainingTemplate.trainingTemplate.sendFeedbackLink;
    this.trainingTemplateForm.sendFeedbackLink =
      trainingTemplate.trainingTemplate.sendFeedbackLink;
    this.trainingTemplateForm.publishingMode = trainingTemplate.trainingTemplate.publishingMode;
    this.trainingTemplateForm.visibilityMode = trainingTemplate.trainingTemplate.visibilityMode;
    this.trainingTemplateForm.allowUnknownUsersToJoin = trainingTemplate.trainingTemplate.allowUnknownUsersToJoin;
    this.trainingTemplateForm.allowSharingWithSubscribers = 
      trainingTemplate.trainingTemplate.allowSharingWithSubscribers;
    this.trainingTemplateForm.maximumMembers =
      trainingTemplate.trainingTemplate.maximumMembers;
    this.trainingTemplateForm.emailBody =
      trainingTemplate.trainingTemplate.emailBody;
    this.trainingTemplateForm.iddTimeMinutes =
      trainingTemplate.trainingTemplate.iddTimeMinutes;
    this.trainingTemplateForm.lernart =
      trainingTemplate.trainingTemplate.lernart;
    this.trainingTemplateForm.lerninhalt =
      trainingTemplate.trainingTemplate.lerninhalt;
    this.trainingTemplateForm.certificateFooterLocationId = 
      trainingTemplate.trainingTemplate.certificateFooterLocationId;
    this.trainingTemplateForm.reminders = [...trainingTemplate.reminders];

    if (
      trainingTemplate.trainingTemplate.trainingLocation &&
      this.trainingTemplateForm.trainingLocation
    ) {
      this.googleSearchText =
        trainingTemplate.trainingTemplate.trainingLocation.location;
      this.trainingTemplateForm.trainingLocation.locationType =
        trainingTemplate.trainingTemplate.trainingLocation.type;
      this.trainingTemplateForm.trainingLocation.location =
        trainingTemplate.trainingTemplate.trainingLocation.location;
      this.trainingTemplateForm.trainingLocation.suffix =
        trainingTemplate.trainingTemplate.trainingLocation.suffix!;
      this.trainingTemplateForm.trainingLocation.placeId =
        trainingTemplate.trainingTemplate.trainingLocation.placeId!;
      this.trainingTemplateForm.trainingLocation.calendarEventId =
        trainingTemplate.trainingTemplate.trainingLocation.calendarEventId ?? null;
      this.trainingTemplateForm.trainingLocation.latitude =
        trainingTemplate.trainingTemplate.trainingLocation.latitude!;
      this.trainingTemplateForm.trainingLocation.longitude =
        trainingTemplate.trainingTemplate.trainingLocation.longitude!;
    }

    if(trainingTemplate.questions) {
      this.trainingTemplateForm.questions = trainingTemplate.questions.map(q => ({
        id: q.id,
        question: q.question,
        translationKey: q.translationKey,
        type: q.type,
        isRequired: q.isRequired,
        templateId: q.templateId,
        maxAdditionalPersons: q.maxAdditionalPersons,
        startHotelDate: q.startHotelDate,
        endHotelDate: q.endHotelDate,
        options: q.options?.map(o => ({
          id: o.id,
          value: o.value,
          translationKey: o.translationKey
        })) ?? null
      }));
    }

    this.trainingTemplateForm.iddHours =
      TimeService.getHoursFromAbsoluteMinutes(
        this.trainingTemplateForm.iddTimeMinutes || 0
      );
    this.trainingTemplateForm.iddMinutes =
      TimeService.getMinutesFromAbsoluteMinutes(
        this.trainingTemplateForm.iddTimeMinutes || 0
      );

    const imageUrl = trainingTemplate.trainingTemplate.imageFile?.fileUrl;

    if (imageUrl) {
      this.loadingService
        .load(
          this.fileUtilService.avatarUrlToBase64(imageUrl)
        )
        .subscribe((base64) => {
          const mimeType = this.fileUtilService.getImageMimeType(imageUrl);
          this.trainingTemplateForm.imageBase64 = `data:${mimeType};base64,${base64}`;
        });
    }

    this.setTreeViewValues();
  }

  refreshComponent() {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([
        '/training-template/edit/' + this.editingTrainingTemplateId,
      ]);
    });
  }

  navigateViewMode(id: string): void {
    this.router.navigate(['/training-template/' + id]);
  }

  getGooglePlaces(e: any) {
    this.googlePlaceinputChanged.next(e.value);
  }

  setLocationPlace(e: any) {
    this.loadingService
      .load(this.mapsService.getDetails(e.itemData.place_id))
      .subscribe((details) => {
        const location = details.formatted_address.startsWith(details.name)
          ? details.formatted_address
          : details.name + ', ' + details.formatted_address;

        if(this.trainingTemplateForm.trainingLocation) {
          this.trainingTemplateForm.trainingLocation.location = location;
          this.trainingTemplateForm.trainingLocation.placeId = details.place_id;
          this.trainingTemplateForm.trainingLocation.calendarEventId = null;
          this.trainingTemplateForm.trainingLocation.latitude =
            details.geometry.location.lat;
          this.trainingTemplateForm.trainingLocation.longitude =
            details.geometry.location.lng;
        }

        this.trainingTemplateForm.trainingLocation = {
          ...this.trainingTemplateForm.trainingLocation,
        } as TrainingLocationCreationAndUpdateModel;
      });
  }

  autocompleteSearchFilter(e: any) {
    return this.googleSearchText;
  }

  onEditClick() {
    this.router.navigate([
      'training-template',
      'edit',
      this.editingTrainingTemplateId,
    ]);
  }

  duplicateTemplate() {
    this.router.navigate(['/training-template/add'], {
      state: { duplicateId: this.editingTrainingTemplateId },
    });
  }

  onDescriptionTabChange(e: DxTabsTypes.ItemClickEvent) {
    this.selectedDescriptionTab = e.itemData.id;
  }

  showCertificatePreview() {
    if(this.isViewMode && this.editingTrainingTemplateId) {
      const key: ReportKeyModel = {
        type: ReportKeyTypeModel.Event,
        id: this.editingTrainingTemplateId
      };

      const encoded = btoa(JSON.stringify(key));

      this.reportUrl = `CertificateReport:${encoded}`;
      this.showPreviewReportViewer = true;
    } else {
      let isFormValid = this.isFormValid();
      
      if(isFormValid) {
        const trainingTemplate = {
          ...this.trainingTemplateForm,
          imageBase64: this.trainingTemplateForm.imageBase64
            ? this.trainingTemplateForm.imageBase64!.split(',')[1]
            : null,
        };

        if (trainingTemplate.iddHours && trainingTemplate.iddMinutes) {
          delete trainingTemplate.iddHours;
          delete trainingTemplate.iddMinutes;
        }

        if (this.trainingTemplateForm.trainingType === 0) {
          trainingTemplate.iddTimeMinutes =
            this.trainingTemplateForm.iddHours! * 60 +
            this.trainingTemplateForm.iddMinutes!;
        } else {
          trainingTemplate.iddTimeMinutes = 0;
          trainingTemplate.lernart = null;
          trainingTemplate.lerninhalt = null;
        }

        this.loadingService
          .load(
            this.reportPreviewService.createTrainingTemplatePreview(trainingTemplate)
          )
          .subscribe((res: PreviewResponse) => {
            const key: ReportKeyModel = {
              type: ReportKeyTypeModel.Preview,
              id: res.previewId
            };

            const encoded = btoa(JSON.stringify(key));

            this.reportUrl = `CertificateReport:${encoded}`;
            this.showPreviewReportViewer = true;
          })
      }
    }
  }

  onReminderSelectionChanged(e: any) {
    if (e.node.selected) {
      this.trainingTemplateForm.reminders = [
        ...this.trainingTemplateForm.reminders,
        e.node.key,
      ];
    } else {
      this.trainingTemplateForm.reminders =
        this.trainingTemplateForm.reminders.filter((id) => id !== e.node.key);
    }
  }

  onExtendedDescriptionContentChanged(content: string) {
    this.trainingTemplateForm.descriptionExtended = content;
  }

  onEmailBodyContentChanged(content: string) {
    this.trainingTemplateForm.emailBody = content;
  }
}
